import { FC, PropsWithChildren, useContext, useEffect, useRef, useState } from "react";

import { Stack } from "@mui/material";

import { SearchTextField } from "@ui/index";

import { ECatalogue } from "@pages/Catalog/components/utils";
import { catalogTabs } from "@pages/Catalog/constants";
import { CatalogPageContext } from "@pages/Catalog/context";

import styled, { keyframes } from "styled-components";

import { AccountType, useAccountType } from "@/contexts/AccountTypeContext";
import { visibleSortValues } from "@/utils/constants";

type TMainContentMobile = {
  isToggleBetween: React.MutableRefObject<boolean>;
  catalogRef: React.MutableRefObject<HTMLElement>;
  isSearchLoading?: boolean;
} & PropsWithChildren;

/* ── Mobile /music chrome — mirrors the cleaned-up desktop layout:
   Browse Music header + smart search + Tracks/Albums tabs + sort + track list.
   Filters (button + chips) and quick-filter categories are intentionally gone;
   discovery runs through smart search. The track rows (children) are untouched. */

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const PageTitle = styled.h1`
  font-family: "Cormorant Garamond", serif;
  font-weight: 500;
  font-size: 1.7rem;
  color: #fff;
  letter-spacing: 0.3px;
  margin: 0;
`;

/* blue accent so it reads as a distinct feature, not a control */
const TestPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(156, 211, 253, 0.1);
  border: 1px solid rgba(156, 211, 253, 0.3);
  border-radius: 20px;
  padding: 7px 13px;
  font-family: "Raleway", sans-serif;
  font-size: 0.72rem;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0.2px;
  color: #9cd3fd;
  white-space: nowrap;
  cursor: pointer;

  svg {
    width: 13px;
    height: 15px;
    stroke: #9cd3fd;
    fill: none;
    stroke-width: 1.8;
  }
`;

/* the hero: a clear, prominent search field (no inner shadow) */
const SearchBox = styled.div`
  .MuiOutlinedInput-root {
    background: rgba(0, 0, 0, 0.16) !important;
    border: 1px solid rgba(186, 144, 92, 0.28) !important;
    border-radius: 12px !important;
    min-height: 50px;
    transition: border-color 0.2s ease;

    &.Mui-focused {
      border-color: rgba(255, 210, 155, 0.65) !important;
    }
  }
  .MuiOutlinedInput-input {
    color: #fbf4ec !important;

    &::placeholder {
      color: rgba(251, 244, 236, 0.5) !important;
    }
  }
  .MuiOutlinedInput-notchedOutline {
    border: none !important;
  }
  .MuiSvgIcon-root {
    color: #ffdca8 !important;
  }
`;

const searchNoticeFadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const SearchNotice = styled.div`
  margin: 12px 0 0;
  padding: 11px 14px;
  border: 1px solid rgba(235, 194, 142, 0.35);
  background: rgba(235, 194, 142, 0.08);
  border-radius: 10px;
  color: #f5dfc4;
  font-family: "Raleway", sans-serif;
  font-size: 0.85rem;
  line-height: 1.45;
  animation: ${searchNoticeFadeIn} 0.4s ease both;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

/* same pill-tab look as desktop: subtle group, soft active segment, blue icon */
const Tabs = styled.div`
  display: flex;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(186, 144, 92, 0.2);
  border-radius: 8px;
  padding: 3px;
  gap: 4px;
`;

const TabBtn = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
  border: none;
  border-radius: 6px;
  background: ${({ $active }) => ($active ? "rgba(255, 220, 168, 0.15)" : "transparent")};
  color: #FBF4EC;
  font-family: "Raleway", sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;

  svg {
    width: 16px;
    height: 16px;
  }
  svg path {
    fill: ${({ $active }) => ($active ? "#9cd3fd" : "#e0d2c1")};
    transition: fill 0.2s ease;
  }
`;

/* sort control — custom dropdown so it's 1:1 with desktop's look AND always
   right-aligned to the trigger (the shared Select positions left-aligned via
   JS, which pushes the menu off-screen on the right edge of a phone). */
const Sort = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const SortTrigger = styled.button`
  position: relative;
  height: 42px;
  padding: 0 32px 0 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(186, 144, 92, 0.2);
  border-radius: 8px;
  color: #e0d2c1;
  font-family: "Raleway", sans-serif;
  font-size: 0.875rem;
  letter-spacing: 0.02em;
  white-space: nowrap;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &::after {
    content: "";
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    width: 10px;
    height: 6px;
    background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%23AC8D75' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
  }
`;

const SortMenu = styled.div<{ $open: boolean }>`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 170px;
  background: #6b4e37;
  border: 1px solid rgba(235, 194, 142, 0.28);
  border-radius: 10px;
  padding: 6px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);
  z-index: 30;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
  transform: translateY(${({ $open }) => ($open ? "0" : "-4px")});
  transition: opacity 0.2s ease, transform 0.2s ease;
`;

const SortOpt = styled.button<{ $active: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: 10px 14px;
  border: none;
  background: transparent;
  border-radius: 6px;
  color: ${({ $active }) => ($active ? "#ffffff" : "#e0d2c1")};
  font-family: "Raleway", sans-serif;
  font-size: 0.875rem;
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: #5c412a;
    color: #ffffff;
  }
`;

/* dark content panel — same as desktop's ContentPanel/TracksArea */
const Panel = styled.div`
  background: rgba(75, 55, 39, 0.35);
  border: 1px solid rgba(186, 144, 92, 0.08);
  border-radius: 12px;
  overflow: hidden;
  padding: 10px 8px 12px;
`;

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

export const MainContentMobile: FC<TMainContentMobile> = ({ isToggleBetween, catalogRef, children, isSearchLoading }) => {
  const { t, state, dispatchReducer, handlers, localSearch, noResultsQuery } = useContext(CatalogPageContext);
  const { handleToggleSwitch, handleSearchChange, handleSearchSubmit } = handlers;
  // Entitlements, not plan identity: an account awaiting reactivation is on
  // the subscription plan but is not paying for it, so it browses and
  // downloads on free terms until it reactivates (ToU 17.4).
  const { entitlementType: accountType } = useAccountType();
  const isSub = accountType === AccountType.SUBSCRIPTION;
  const isMusicCreator = accountType === AccountType.MUSIC_CREATOR;

  const sortOptions = visibleSortValues(t);
  const currentSort = sortOptions.find((o) => o.value === state.sortBy.value) ?? sortOptions[0];

  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!sortOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) setSortOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [sortOpen]);

  return (
    <Stack gap="16px" ref={catalogRef}>
      <Head>
        <PageTitle>Browse Music</PageTitle>
        {!isMusicCreator && (
          <TestPill
            onClick={() =>
              dispatchReducer({
                type: ECatalogue.OPEN_QUESTION_FLOW,
                payload: true,
              })
            }
          >
            <ShieldIcon />
            {isSub ? "Subscription Test" : "Free Use? Test"}
          </TestPill>
        )}
      </Head>

      {/* The scroll target after a search arrives by URL (a tag or artist link
          from elsewhere on the site). Without it the page scrolls to the top of
          the whole catalogue block, which sits ABOVE the heading — and by the
          time the results render the search box has scrolled off, leaving the
          Tracks/Albums tabs at the top of the screen and the search term
          invisible. The visitor cannot see what was searched for. */}
      <SearchBox data-catalog-search>
        <SearchTextField
          value={localSearch ?? ""}
          handleChange={handleSearchChange}
          placeholder="What are you looking for?"
          variant="dark"
          loading={!!isSearchLoading}
          iconOffsetY={1}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSearchSubmit();
          }}
        />
      </SearchBox>

      {noResultsQuery && (
        <SearchNotice>
          No results for your search. Below are some other tracks for you to explore.
        </SearchNotice>
      )}

      <Controls>
        <Tabs>
          {catalogTabs.map(({ value, label, icon }) => (
            <TabBtn
              key={value}
              $active={state.catalogTab === value}
              onClick={() => {
                isToggleBetween.current = true;
                handleToggleSwitch(value);
              }}
            >
              {icon}
              {t(`${label}.desktop`)}
            </TabBtn>
          ))}
        </Tabs>

        <Sort ref={sortRef}>
          <SortTrigger type="button" onClick={() => setSortOpen((o) => !o)}>
            {currentSort.title}
          </SortTrigger>
          <SortMenu $open={sortOpen}>
            {sortOptions.map((option) => (
              <SortOpt
                key={option.value}
                type="button"
                $active={option.value === state.sortBy.value}
                onClick={() => {
                  dispatchReducer({ type: ECatalogue.SortBy, payload: option });
                  setSortOpen(false);
                }}
              >
                {option.title}
              </SortOpt>
            ))}
          </SortMenu>
        </Sort>
      </Controls>

      <Panel>{children}</Panel>
    </Stack>
  );
};
